import * as ts from "typescript"

const validReactTypes = [
  "FC",
  "FunctionComponent",
  "Component",
  "Element",
  "ReactElement",
  "ForwardRefExoticComponent",
  "ReactNode",
]
const validReactComponentTypeAnnotations = new Set([
  "ComponentType",
  "FC",
  "ForwardRefExoticComponent",
  "FunctionComponent",
  "MemoExoticComponent",
])

function isPascalCaseSymbolName(name: string): boolean {
  return name === "default" || /^[A-Z]/.test(name)
}

function hasReactRenderSyntax(node: ts.Node): boolean {
  let containsJsx = false
  let hasReactCreateElementCall = false
  const visit = (current: ts.Node): void => {
    if (containsJsx && hasReactCreateElementCall) {
      return
    }

    if (ts.isJsxElement(current) || ts.isJsxSelfClosingElement(current) || ts.isJsxFragment(current)) {
      containsJsx = true
    }

    if (
      ts.isCallExpression(current) &&
      ts.isPropertyAccessExpression(current.expression) &&
      current.expression.expression.getText() === "React" &&
      current.expression.name.getText() === "createElement"
    ) {
      hasReactCreateElementCall = true
    }

    ts.forEachChild(current, visit)
  }

  visit(node)
  return containsJsx || hasReactCreateElementCall
}

function isFunctionLikeWithReactOutput(node: ts.Node): boolean {
  if (
    ts.isFunctionDeclaration(node) ||
    ts.isFunctionExpression(node) ||
    ts.isArrowFunction(node) ||
    ts.isMethodDeclaration(node)
  ) {
    return hasReactRenderSyntax(node)
  }

  return false
}

function getTypeAnnotationName(node: ts.TypeNode): string | null {
  if (!ts.isTypeReferenceNode(node)) {
    return null
  }

  const { typeName } = node
  if (ts.isIdentifier(typeName)) {
    return typeName.text
  }

  if (ts.isQualifiedName(typeName) && typeName.left.getText() === "React") {
    return typeName.right.text
  }

  return null
}

function isReactComponentTypeAnnotation(node: ts.TypeNode): boolean {
  const typeName = getTypeAnnotationName(node)
  return typeName ? validReactComponentTypeAnnotations.has(typeName) : false
}

function hasRenderMethodWithJsx(classNode: ts.ClassDeclaration): boolean {
  for (const member of classNode.members) {
    if (!ts.isMethodDeclaration(member)) {
      continue
    }

    const methodName = member.name?.getText()
    if (methodName !== "render") {
      continue
    }

    if (hasReactRenderSyntax(member)) {
      return true
    }
  }

  return false
}

function unwrapExpression(node: ts.Expression): ts.Expression {
  let current = node

  while (
    ts.isParenthesizedExpression(current) ||
    ts.isAsExpression(current) ||
    ts.isSatisfiesExpression(current) ||
    ts.isTypeAssertionExpression(current) ||
    ts.isNonNullExpression(current)
  ) {
    current = current.expression
  }

  return current
}

function resolveAliasedSymbol(symbol: ts.Symbol, checker: ts.TypeChecker): ts.Symbol {
  // eslint-disable-next-line no-bitwise
  return symbol.flags & ts.SymbolFlags.Alias ? checker.getAliasedSymbol(symbol) : symbol
}

function reactWrapperName(node: ts.Expression): string | null {
  const expression = unwrapExpression(node)

  if (ts.isIdentifier(expression)) {
    return expression.text
  }

  if (ts.isPropertyAccessExpression(expression) && ts.isIdentifier(expression.expression)) {
    return `${expression.expression.text}.${expression.name.text}`
  }

  return null
}

function isForwardRefOrMemoCall(node: ts.CallExpression, checker: ts.TypeChecker, seen: Set<ts.Symbol>): boolean {
  const expressionText = reactWrapperName(node.expression)
  if (
    expressionText !== "forwardRef" &&
    expressionText !== "memo" &&
    expressionText !== "React.forwardRef" &&
    expressionText !== "React.memo"
  ) {
    return false
  }

  const firstArg = node.arguments[0]
  if (!firstArg) {
    return false
  }

  return isReactComponentExpression(firstArg, checker, seen)
}

function isReactComponentCall(node: ts.CallExpression, checker: ts.TypeChecker, seen: Set<ts.Symbol>): boolean {
  return isForwardRefOrMemoCall(node, checker, seen)
}

function isReactComponentExpression(node: ts.Expression, checker: ts.TypeChecker, seen: Set<ts.Symbol>): boolean {
  const expression = unwrapExpression(node)

  if (isFunctionLikeWithReactOutput(expression)) {
    return true
  }

  if (ts.isCallExpression(expression)) {
    return isReactComponentCall(expression, checker, seen)
  }

  if (ts.isIdentifier(expression)) {
    const symbol = checker.getSymbolAtLocation(expression)
    if (!symbol) {
      return false
    }

    return isReactComponentBySyntax(resolveAliasedSymbol(symbol, checker), checker, seen, false)
  }

  return false
}

function isReactComponentBySyntax(
  symbol: ts.Symbol,
  checker: ts.TypeChecker,
  seen: Set<ts.Symbol>,
  requirePascalCase = true,
): boolean {
  if (seen.has(symbol)) {
    return false
  }

  seen.add(symbol)

  if (requirePascalCase && !isPascalCaseSymbolName(symbol.getName())) {
    return false
  }

  for (const declaration of symbol.declarations ?? []) {
    if (isFunctionLikeWithReactOutput(declaration)) {
      return true
    }

    if (ts.isVariableDeclaration(declaration) && declaration.initializer) {
      if (declaration.type && isReactComponentTypeAnnotation(declaration.type)) {
        return true
      }

      const initializer = unwrapExpression(declaration.initializer)

      if (isFunctionLikeWithReactOutput(initializer)) {
        return true
      }

      if (ts.isCallExpression(initializer) && isReactComponentCall(initializer, checker, seen)) {
        return true
      }
    }

    if (ts.isClassDeclaration(declaration) && hasRenderMethodWithJsx(declaration)) {
      return true
    }

    if (ts.isExportAssignment(declaration)) {
      const expression = unwrapExpression(declaration.expression)

      if (isFunctionLikeWithReactOutput(expression)) {
        return true
      }

      if (ts.isCallExpression(expression) && isReactComponentCall(expression, checker, seen)) {
        return true
      }
    }
  }

  return false
}

/**
 * Helper function to check if type is from React
 */
export function isTypeFromReact(type: ts.Type, checker: ts.TypeChecker): boolean {
  if (type.isUnion()) {
    // in case the return type is a union we need to check if any of the types are from react
    return type.types.some(t => isTypeFromReact(t, checker))
  }

  if (type.symbol && type.symbol.declarations && type.symbol.declarations.length > 0) {
    const declaration = type.symbol.declarations[0]
    const sourceFile = declaration.getSourceFile()
    const { fileName } = sourceFile

    const typeString = checker.typeToString(type)

    const isReactType = Boolean(typeString.match(new RegExp(`^(React[.])?(?:${validReactTypes.join("|")}).*`))?.length)

    // Check if it's from react package
    return isReactType && fileName.includes("@types/react")
  }

  return false
}

/**
 * Helper function to check if a symbol is a React component. It checks cases when react component is a function or reassigned to different variable.
 */
export function isReactComponent(symbol: ts.Symbol, _sourceFile: ts.SourceFile, checker: ts.TypeChecker) {
  // Get the actual symbol if it's an alias - component reassigned to different variable
  const actualSymbol = resolveAliasedSymbol(symbol, checker)

  const type = checker.getTypeOfSymbol(actualSymbol)

  if (isTypeFromReact(type, checker)) {
    return true
  }

  // check case when it might be a function
  const [signature] = type.getCallSignatures()

  if (signature) {
    const returnType = checker.getReturnTypeOfSignature(signature)
    if (isTypeFromReact(returnType, checker)) {
      return true
    }
  }

  return isReactComponentBySyntax(actualSymbol, checker, new Set())
}
